import type {
  DraftPayment,
  ExportHistoryType,
  InvoiceData,
  InvoiceTag,
  Payment,
  PaymentDetails,
  Polygon,
  Supplier,
  Transaction,
} from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";

export const useGetInvoice = ({
  invoiceId,
  fetchPolicy,
}: {
  invoiceId: string;
  fetchPolicy?: "network-only";
}) => {
  const { tenantId } = useTenant();

  const { data, error, loading, refetch } =
    useQuery<InvoiceDetailsQueryResponse>(INVOICE_QUERY, {
      variables: { tenantId, invoiceId },
      fetchPolicy,
    });

  return { invoice: data, error, loading, refetch };
};

export const INVOICE_QUERY = gql`
  query InvoiceDetails($invoiceId: String!) {
    invoiceDetails(invoiceId: $invoiceId) {
      invoiceDetailsId
      invoice {
        invoiceId
        invoiceText
        isNettoInvoice
        aiSummary
        totalAmount
        totalAmountConfidence
        currency
        dueDate
        serviceDateConfidence
        serviceDate
        invoiceDate
        invoiceDateConfidence
        invoiceNoConfidence
        invoiceNo
        invoiceSource
        fileBlobName
        supplierId
        fileName
        manualProcessing
        ocrReviewed
        createdAt
        bookingPeriod
        supplierMatch
        supplierMatchConfidence
        status
        processingStatus
        split
        reviewType
        createDatevPayment
        isDigitalInvoice
        isBooking
        vatId
        vatIdConfidence
        invoiceText
        errorReason {
          code
          message
          data
        }
        validationErrors
        isDuplicate
        isPaid
        smbReviewStatus
        costCenterIds
        iban
        isSingleInvoice
        reconTriggered
        type
      }
      tags {
        tagId
        tagName
        llmRule
        details
        approval
        createdAt
        updatedAt
      }
      duplicates {
        invoiceId
        status
        currency
        totalAmount
        invoiceDate
        invoiceNo
        createdAt
        supplierName
      }
      exports {
        datevExportId
        results
        exportedAt
      }
      invoicePolygon {
        invoicePolygonId
        invoiceId
        polygonData
      }
      supplier {
        supplierId
        supplierName
        status
        datevAccountNumber
        paymentDetails {
          paymentDetailsId
          IBAN
        }
      }
      payments {
        bankTxnProjectionId
        bankTxnId
        tenantId
        status
        amount
        createdAt
        recipientName
        recipientIban
        sequenceNumber
        senderIban
        currency
        purpose
        executionDate
      }
      draftPayments {
        draftPaymentId
        tenantId
        status
        amount
        rawAmount
        currency
        createdAt
        recipientName
        recipientIban
        purpose
        executionDate
        discount
      }
      transactions {
        bankTxnId
        side
        amount
        bookingDate
        smbAttachmentStatus
        purpose
        counterpartyName
        createdAt
        currency
        originalAmount
        originalCurrency
      }
    }
  }
`;

export type GetInvoiceDetailsInvoice = Pick<
  InvoiceData,
  | "invoiceId"
  | "invoiceText"
  | "isNettoInvoice"
  | "aiSummary"
  | "totalAmount"
  | "totalAmountConfidence"
  | "currency"
  | "dueDate"
  | "serviceDateConfidence"
  | "serviceDate"
  | "invoiceDate"
  | "invoiceDateConfidence"
  | "invoiceNoConfidence"
  | "invoiceNo"
  | "invoiceSource"
  | "fileBlobName"
  | "supplierId"
  | "fileName"
  | "ocrReviewed"
  | "createdAt"
  | "bookingPeriod"
  | "supplierMatch"
  | "supplierMatchConfidence"
  | "status"
  | "processingStatus"
  | "split"
  | "reviewType"
  | "createDatevPayment"
  | "isDigitalInvoice"
  | "isBooking"
  | "vatId"
  | "vatIdConfidence"
  | "errorReason"
  | "validationErrors"
  | "isDuplicate"
  | "isPaid"
  | "smbReviewStatus"
  | "costCenterIds"
  | "iban"
  | "isSingleInvoice"
  | "type"
> & { manualProcessing: boolean; reconTriggered?: boolean };

export type InvoiceDetailsPayment = Pick<
  Payment,
  | "bankTxnId"
  | "tenantId"
  | "status"
  | "amount"
  | "createdAt"
  | "recipientName"
  | "recipientIban"
  | "currency"
  | "purpose"
  | "executionDate"
> & {
  bankTxnProjectionId: string;
  sequenceNumber: number;
  senderIban: string | null;
};

export type InvoiceDetailsSupplier = Pick<
  Supplier,
  "supplierId" | "supplierName" | "status" | "datevAccountNumber"
> & {
  paymentDetails: Pick<PaymentDetails, "paymentDetailsId" | "IBAN">;
};

export type InvoiceDetailsDraftPayment = Pick<
  DraftPayment,
  | "draftPaymentId"
  | "tenantId"
  | "status"
  | "amount"
  | "rawAmount"
  | "currency"
  | "createdAt"
  | "recipientName"
  | "recipientIban"
  | "purpose"
  | "executionDate"
  | "discount"
>;

export type GetInvoiceDetails = {
  invoiceDetailsId: string;
  invoice: GetInvoiceDetailsInvoice;
  tags: Pick<
    InvoiceTag,
    | "tagId"
    | "tagName"
    | "llmRule"
    | "details"
    | "approval"
    | "createdAt"
    | "updatedAt"
  >[];
  duplicates: DuplicateInvoice[];
  exports: (Pick<ExportHistoryType, "datevExportId" | "exportedAt"> & {
    results: unknown[];
  })[];
  invoicePolygon: {
    invoicePolygonId: string;
    invoiceId: string;
    polygonData: Polygon;
  };
  supplier: InvoiceDetailsSupplier;
  payments: InvoiceDetailsPayment[];
  draftPayments: InvoiceDetailsDraftPayment[];
  transactions: InvoiceDetailsTransaction[];
};

export type InvoiceDetailsTransaction = Pick<
  Transaction,
  | "bankTxnId"
  | "side"
  | "amount"
  | "bookingDate"
  | "smbAttachmentStatus"
  | "purpose"
  | "counterpartyName"
  | "createdAt"
  | "currency"
  | "originalAmount"
  | "originalCurrency"
>;

export type InvoiceDetailsQueryResponse = {
  invoiceDetails: GetInvoiceDetails;
};

export type DuplicateInvoice = {
  invoiceId: string;
  status: string;
  currency: string;
  totalAmount: number;
  invoiceDate: Date;
  invoiceNo: string;
  createdAt: Date;
  supplierName: string;
};
